import { eq, useLiveQuery } from '@tanstack/react-db';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import {
  Link,
  createFileRoute,
  notFound,
  useRouteContext,
} from '@tanstack/react-router';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { Suspense, useEffect } from 'react';

import { BesluitSchema, HoofdstukSchema } from '@/api';
import { useBesluitId } from '@/api/queries/besluit';
import {
  LinkParagraaf,
  LinkSubparagraaf,
  useHoofdstukHB,
} from '@/api/queries/objects';
import {
  ensureQueryDataHoofdstukByLineageId,
  useHoofdstukByLineageId,
} from '@/api/queries/objects/selectors/useHoofdstukByLineageId';
import { DropdownMenu } from '@/components/DropdownMenu/DropdownMenu';
import { ModalTrigger } from '@/components/Modal';
import { Warning, WarningProps } from '@/components/Warning';
import { RegularSpinner } from '@/icons/components';
import { JumpToParagraafFetch } from '@/modals/JumpToParagraaf';
import { useUserStore } from '@/stores/user';
import { useOffsetTop } from '@/utils/hooks/useOffsetTop';

import { Hoofdstuk } from './-components/Hoofdstuk/Hoofdstuk';
import { HoofdstukProvider } from './-components/Hoofdstuk/hoofdstukContext';
import { Paragraaf } from './-components/Paragraaf/Paragraaf';
import { ParagraafSkeleton } from './-components/Paragraaf/ParagraafSkeleton';
import { SidebarRight } from './-components/SidebarRight/SidebarRight';
import { Slot } from './-components/Slot/Slot';
import { Subparagraaf } from './-components/Subparagraaf/Subparagraaf';
import { Toolbar } from './-components/Toolbar/Toolbar';
import { Voorschrift } from './-components/Voorschrift/Voorschrift';
import {
  ensureQueryDataEditorLinksWithLabel,
  useEditorLinksWithLabelSuspense,
} from './-hooks/useEditorLinksWithLabel';
import { SidebarLeftTab, useEditorStore } from './-store/useEditorStore';
import {
  LabelEditorFilter,
  useLabelEditorStore,
} from './-store/useLabelEditorStore';
import { getObjectState } from './-utils/getObjectState';

export const Route = createFileRoute(
  '/$bedrijfLineageId/editor/$besluitId/labels/$hoofdstukLineageId'
)({
  component: RouteComponent,
  onEnter: () => {
    useEditorStore.getState().setSidebarLeftTabId(SidebarLeftTab.FILTER);
  },
  onLeave: () => {
    useEditorStore.getState().clearSelectedObject();
  },
  loader: async ({
    context: { queryClient },
    params: { hoofdstukLineageId, besluitId },
  }) => {
    const [besluit, hoofdstuk] = await Promise.all([
      useBesluitId.ensureQueryData(besluitId),
      ensureQueryDataHoofdstukByLineageId(hoofdstukLineageId, queryClient),
    ]);

    if (!hoofdstuk || !besluit) throw notFound();

    await Promise.all([
      queryClient.ensureQueryData(
        useHoofdstukHB.getOptions({ besluitId, hoofdstukId: hoofdstuk.ID })
      ),
      ensureQueryDataEditorLinksWithLabel(queryClient, {
        hoofdstukId: hoofdstuk.ID,
        besluitId,
        labelFilter: useLabelEditorStore.getState().filter,
        multiSelectVoorschriftIdLog: new Set<string>(),
      }),
    ]);
  },
});

function RouteComponent() {
  const { hoofdstukLineageId, besluitId } = Route.useParams();
  const labelFilter = useLabelEditorStore((state) => state.filter);

  /**
   * Fetch data
   */
  const [hoofdstuk, besluit] = useSuspenseQueries({
    queries: [
      useHoofdstukByLineageId.getOptions(hoofdstukLineageId),
      useBesluitId.getOptions(besluitId),
    ],
  });

  if (!hoofdstuk.data) {
    return <LabelWarning variant="warning" title="Geen hoofdstuk gevonden" />;
  }

  if (
    (labelFilter.type === 'labels' && labelFilter.labelIds.length === 0) ||
    !labelFilter
  ) {
    return (
      <LabelWarning
        variant="info"
        title="Er is geen label geselecteerd"
        description='Klik op de "filteren op labels" knop om te beginnen'
      />
    );
  }

  return (
    <div className="bg-gray-50">
      <Suspense
        fallback={
          <div className="flex h-screen w-full items-center justify-center">
            <RegularSpinner className="mx-auto animate-spin" />
          </div>
        }
      >
        <HoofdstukWithLabels
          besluit={besluit.data}
          hoofdstuk={hoofdstuk.data}
          labelFilter={labelFilter}
        />
      </Suspense>
    </div>
  );
}

const HoofdstukWithLabels = ({
  besluit,
  hoofdstuk,
  labelFilter,
}: {
  besluit: BesluitSchema;
  hoofdstuk: HoofdstukSchema;
  labelFilter: LabelEditorFilter;
}) => {
  const { bedrijfLineageId } = Route.useParams();
  const multiSelectVoorschriftIdLog = useEditorStore(
    (state) => state.multiSelectVoorschriftIdLog
  );

  const filteredLinks = useEditorLinksWithLabelSuspense({
    hoofdstukId: hoofdstuk.ID,
    besluitId: besluit.ID,
    labelFilter,
    multiSelectVoorschriftIdLog,
  });

  const { data: hb } = useSuspenseQuery(
    useHoofdstukHB.getOptions({
      besluitId: besluit.ID,
      hoofdstukId: hoofdstuk.ID,
    })
  );

  const { hoofdstukReserveringCollection } = useRouteContext({
    from: '/$bedrijfLineageId/editor/$besluitId',
  });
  const hoofdstukReserveringResult = useLiveQuery(
    (q) =>
      q
        .from({ hoofdstukReservering: hoofdstukReserveringCollection })
        .where(({ hoofdstukReservering }) =>
          eq(hoofdstukReservering.Reservering_lineage, hoofdstuk.Lineage)
        )
        .findOne(),
    [hoofdstuk.Lineage]
  );

  /**
   * Check if user has reservering
   */
  const userId = useUserStore((state) => state.user?.ID);
  const userHasReservering =
    hoofdstukReserveringResult.data?.Gebruiker === userId;

  /**
   * Virtualizer
   */
  const { ref: listRef, offsetTop } = useOffsetTop();
  const virtualizer = useWindowVirtualizer({
    count: filteredLinks.length,
    estimateSize: () => 1000,
    overscan: 1,
    enabled: !!filteredLinks.length,
    scrollMargin: offsetTop,
    paddingEnd: 300,
  });

  const setScrollToParagraaf = useLabelEditorStore(
    (s) => s.setScrollToParagraaf
  );
  useEffect(() => {
    setScrollToParagraaf((paragraafId: string) => {
      const index = filteredLinks.findIndex(
        (link) => link.PHB.Paragraaf === paragraafId
      );
      if (index !== -1) {
        virtualizer.scrollToIndex(index, { align: 'start' });
      }
    });

    return () => setScrollToParagraaf(null);
  }, [filteredLinks, virtualizer, setScrollToParagraaf]);

  const virtualItems = virtualizer.getVirtualItems();
  const hoofdstukState = getObjectState(hoofdstuk);

  /**
   * Null-check hb (should never happen)
   */
  if (!hb) {
    return null;
  }

  if (filteredLinks.length === 0) {
    return (
      <LabelWarning
        title="Geen resultaten"
        description="Er zijn geen voorschriften gevonden met de geselecteerde filter(s)."
        variant="warning"
      />
    );
  }

  return (
    <HoofdstukProvider
      hoofdstuk={hoofdstuk}
      hb={hb}
      userHasReservering={userHasReservering}
      besluit={besluit}
      grondslagHasStandaardteksten={false}
      links={filteredLinks}
      hoofdstukState={hoofdstukState}
    >
      <div className="container-default relative mx-auto min-h-screen px-4 pt-4">
        <Toolbar
          includeViews={false}
          optionsMenuContent={
            <ModalTrigger
              modalComponent={JumpToParagraafFetch}
              onOpen={() => ({
                onClick: ({ index }) => {
                  virtualizer.scrollToIndex(index, { align: 'start' });
                },
                paragraafIds: filteredLinks.map((link) => link.PHB.Paragraaf),
                hoofdstukNummer: hoofdstuk.Nummer,
                stateIconOptions: {
                  besluitTypeId: besluit.Type,
                },
              })}
              asChild
            >
              <DropdownMenu.Button disabled={filteredLinks.length === 0}>
                Alle paragrafen
              </DropdownMenu.Button>
            </ModalTrigger>
          }
        />

        <div
          className="relative w-full"
          style={{
            height: `${virtualizer.getTotalSize()}px`,
          }}
          ref={listRef}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              paddingTop: virtualItems.length ? 0 : `${offsetTop}px`,
              transform: `translateY(${(virtualItems[0]?.start ?? 0) - virtualizer.options.scrollMargin}px)`,
            }}
          >
            <Hoofdstuk />

            {virtualizer.getVirtualItems().map((item) => {
              const paragraafLink = filteredLinks[item.index]!;
              const testId = `paragraaf-${item.index}`;

              return (
                <div key={item.key}>
                  <Suspense
                    fallback={
                      <ParagraafSkeleton
                        ref={virtualizer.measureElement}
                        data-index={item.index}
                        link={paragraafLink}
                        testId={testId}
                        transparent
                      />
                    }
                  >
                    <Paragraaf
                      ref={virtualizer.measureElement}
                      data-index={item.index}
                      link={paragraafLink}
                      isSelectable={false}
                      testId={testId}
                      transparentBg
                      linkSlot={
                        <Link
                          to="/$bedrijfLineageId/editor/$besluitId/voorschriften/$hoofdstukLineageId"
                          params={{
                            hoofdstukLineageId: hoofdstuk.Lineage,
                            besluitId: besluit.ID,
                            bedrijfLineageId,
                          }}
                          search={{
                            focusParagraafId: paragraafLink.PHB.Paragraaf,
                          }}
                        >
                          Bekijk ongefilterde paragraaf
                        </Link>
                      }
                    >
                      <Subparagrafen
                        paragraafLink={paragraafLink}
                        paragraafIndex={item.index}
                      />
                    </Paragraaf>
                  </Suspense>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {hb.Type === 'MBIO' && (
        <Slot
          type="hoofdstuk"
          state={hoofdstukState}
          id={hoofdstuk.ID}
          messageClassName="mt-[10rem]"
          className="fixed inset-0"
        />
      )}

      <SidebarRight
        hoofdstuk={hoofdstuk}
        hb={hb}
        userHasReservering={userHasReservering}
      />
    </HoofdstukProvider>
  );
};

const Subparagrafen = ({
  paragraafLink,
  paragraafIndex,
}: {
  paragraafLink: LinkParagraaf;
  paragraafIndex: number;
}) => {
  return (
    <div className="flex flex-col gap-2">
      {paragraafLink.subparagrafen.map(
        (subparagraafLink, subparagraafIndex) => {
          return (
            <Subparagraaf
              key={`subparagraaf-${subparagraafLink.SPPB.ID}`}
              link={subparagraafLink}
              testId={`subparagraaf-${paragraafIndex}-${subparagraafIndex}`}
              isSelectable={false}
              transparentBg
            >
              <Voorschriften
                subparagraafIndex={subparagraafIndex}
                paragraafIndex={paragraafIndex}
                subparagraafLink={subparagraafLink}
              />
            </Subparagraaf>
          );
        }
      )}
    </div>
  );
};

const Voorschriften = ({
  subparagraafLink,
  paragraafIndex,
  subparagraafIndex,
}: {
  subparagraafLink: LinkSubparagraaf;
  paragraafIndex: number;
  subparagraafIndex: number;
}) => {
  return (
    <div className="flex flex-col gap-1">
      {subparagraafLink.voorschriften.map(
        (voorschriftLink, voorschriftIndex) => {
          return (
            <div key={`voorschrift-${voorschriftLink.VSPB.ID}`}>
              <Voorschrift
                link={voorschriftLink}
                testId={`voorschrift-${paragraafIndex}-${subparagraafIndex}-${voorschriftIndex}`}
                highlightSelectedLabel
                isSelectable
              />
            </div>
          );
        }
      )}
    </div>
  );
};

const LabelWarning = ({
  title,
  description,
  variant = 'warning',
}: {
  variant: WarningProps['variant'];
  title: string;
  description?: string;
}) => (
  <div className="container-default mx-auto mt-8 flex min-h-screen items-start justify-center px-4">
    <Warning size="large" variant={variant}>
      <Warning.Title asChild>
        <h2>{title}</h2>
      </Warning.Title>
      {description && <Warning.Description>{description}</Warning.Description>}
    </Warning>
  </div>
);
